import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { FooterStyled } from "./FooterStyled";
import Logo from "../../../../assets/images/saharaText.png";
import LocationMap from "../../../../assets/images/location.png";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const Footer = () => {
  const footerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const footer = footerRef.current;
    if (!footer) return;

    const ctx = gsap.context(() => {
      // Animate footer sections
      gsap.fromTo(
        footer.querySelectorAll(".footerGrid > div"),
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          stagger: 0.1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: footer,
            start: "top 85%",
            toggleActions: "play none none reverse",
          },
        }
      );
    }, footer);

    return () => ctx.revert();
  }, []);

  const currentYear = new Date().getFullYear();

  return (
    <FooterStyled ref={footerRef}>
      {/* Background decorations */}
      <div className="footerBg">
        <div className="gradientOrb orb1" />
        <div className="gradientOrb orb2" />
      </div>

      <div className="footerMain">
        <div className="footerGrid">
          {/* Brand Section */}
          <div className="footerBrand">
            <img src={Logo} alt="Sahara Computers" className="brandLogo" />
            <p className="brandDescription">
              Your trusted technology partner in Kuwait for over 25 years. We
              provide comprehensive IT solutions, hardware, and exceptional
              support services.
            </p>
            <div className="socialLinks">
              <a
                href="https://www.facebook.com/people/SaharaComputers/100065312596899/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                </svg>
              </a>
              <a
                href="https://www.instagram.com/saharacomputerskw/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                </svg>
              </a>
              <a
                href="mailto:sales@saharacomputers.com"
                aria-label="Email"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="footerLinks">
            <h4>Quick Links</h4>
            <ul>
              <li>
                <Link to="/">Home</Link>
              </li>
              <li>
                <Link to="/policy">About Us</Link>
              </li>
              <li>
                <Link to="/#services">Services</Link>
              </li>
              <li>
                <Link to="/products">Products</Link>
              </li>
              <li>
                <Link to="/#contactUs">Contact</Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div className="footerLinks">
            <h4>Our Services</h4>
            <ul>
              <li>
                <Link to="/services/1">IT Hardware</Link>
              </li>
              <li>
                <Link to="/services/2">Software Solutions</Link>
              </li>
              <li>
                <Link to="/services/3">Network Services</Link>
              </li>
              <li>
                <Link to="/services/4">Technical Support</Link>
              </li>
              <li>
                <Link to="/services/5">Maintenance</Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="footerContact">
            <h4>Contact Us</h4>
            <div className="contactItem">
              <div className="contactIcon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
                </svg>
              </div>
              <div className="contactText">
                <div className="contactLabel">Facebook</div>
                <div className="contactValue">
                  <a
                    href="https://www.facebook.com/people/SaharaComputers/100065312596899/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    @saharacomputers
                  </a>
                </div>
              </div>
            </div>
            <div className="contactItem">
              <div className="contactIcon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                  <path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z" />
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                </svg>
              </div>
              <div className="contactText">
                <div className="contactLabel">Instagram</div>
                <div className="contactValue">
                  <a
                    href="https://www.instagram.com/saharacomputerskw/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    @saharacomputerskw
                  </a>
                </div>
              </div>
            </div>
            <div className="contactItem">
              <div className="contactIcon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <div className="contactText">
                <div className="contactLabel">Email</div>
                <div className="contactValue">
                  <a href="mailto:sales@saharacomputers.com">
                    sales@saharacomputers.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Location Map */}
        <div className="footerMap">
          <h4>Our Location</h4>
          <a
            href="https://maps.app.goo.gl/ZZfMaGZy6raCoYm76"
            target="_blank"
            rel="noopener noreferrer"
            className="mapWrapper"
          >
            <img src={LocationMap} alt="Location Map" className="mapImage" />
            <div className="mapOverlay">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
              <span>View on Google Maps</span>
            </div>
          </a>
          <p className="addressText">Kuwait City, Kuwait</p>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="footerBottom">
        <div className="copyright">
          &copy; {currentYear}{" "}
          <a href="/">Sahara National Computer Co. W.L.L</a>. All rights
          reserved.
        </div>
        <div className="bottomLinks">
          <Link to="/privacy-policy">Privacy Policy</Link>
          <Link to="/terms-of-service">Terms of Service</Link>
          <Link to="/support">Support</Link>
        </div>
      </div>
    </FooterStyled>
  );
};

export default Footer;
